import { useEffect, useMemo, useState } from "react";
import { BadgeCheck, Bot, CreditCard, Loader2, MapPin, Plus, ShoppingCart, Sparkles, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { isFramed, openTopLevel } from "@/lib/topNavigation";
import { DurationPicker, LongerPlanBox, useFmt } from "@/components/market/Prepaid";
import { AddBusiness, AdvancedLink, CartIntro, ConfirmList } from "@/components/market/BusinessSetup";
import { ChangeBadges, ChangesBox } from "@/components/market/CartChanges";

const clamp = (n) => Math.max(0, Math.min(50, Number(n) || 0));
const COVER = { vip: "cart.vip", group: "cart.group", custom: "cart.custom", active: "cart.active", account: "cart.account" };

const EmpInput = ({ value, onChange, testId }) => {
  const { t } = useI18n();
  return (
    <label className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-neutral-200">
      <Users className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {t("prepaid.employees")}
      <input type="number" inputMode="numeric" min={0} max={50} value={value} onChange={(e) => onChange(clamp(e.target.value))}
        className="w-20 rounded-lg border border-neutral-700 bg-black/50 px-2 py-1 text-white" data-testid={testId} />
    </label>
  );
};

// Running business: change ITS extra employees (prorated on its own subscription only).
const LiveEmployees = ({ b, testId }) => {
  const { t } = useI18n();
  const [n, setN] = useState(b.employees || 0);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/dev/billing/employees", { quantity: n }, { params: { ref: b.ref, key_id: b.key_id } });
      toast.success(t("cart.empSaved", { ref: b.ref }));
    } catch (err) {
      toast.error(translateError(err, t));
    }
    setBusy(false);
  };
  return (
    <div className="mt-3 min-w-0 space-y-2" data-testid={`${testId}-live-emp`}>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <EmpInput value={n} onChange={setN} testId={`${testId}-live-emp-input`} />
        <button type="button" className="btn-red btn-sm min-h-[44px]" onClick={save} disabled={busy || n === (b.employees || 0)} data-testid={`${testId}-live-emp-save`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t("cart.empSave")}
        </button>
      </div>
      <p className="text-xs text-neutral-400">{t("cart.empProrata")}</p>
    </div>
  );
};

// What the mini-site asked for this business (signed &opts=): the store shows prices, the app none.
const Request = ({ r, testId }) => {
  const { t } = useI18n();
  return (
    <div className="mt-2 flex min-w-0 flex-wrap gap-2 text-xs font-semibold" data-testid={`${testId}-request`}>
      {r.add > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-sky-900/50 px-2 py-0.5 text-sky-100" data-testid={`${testId}-request-emp`}><Users className="h-3.5 w-3.5" /> {t("cart.reqAdd", { a: r.add, t: r.total })}</span>}
      {r.team && <span className="inline-flex items-center gap-1 rounded-full bg-violet-900/50 px-2 py-0.5 text-violet-100" data-testid={`${testId}-request-team`}><MapPin className="h-3.5 w-3.5" /> {t("cart.reqTeam")}</span>}
      {r.robot && <span className="inline-flex items-center gap-1 rounded-full bg-teal-900/50 px-2 py-0.5 text-teal-100" data-testid={`${testId}-request-robot`}><Bot className="h-3.5 w-3.5" /> {t("robot.name")}</span>}
      {r.new && <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/50 px-2 py-0.5 text-amber-100" data-testid={`${testId}-request-new`}><Sparkles className="h-3.5 w-3.5" /> {t("cart.reqNew")}</span>}
    </div>
  );
};

// &retour= of the mini-site link: back to the app after the immediate upgrade (paye=1 charged / paye=0 failed).
const backFrom = (res) => res?.data?.retour || res?.headers?.["x-retour"];

// Running business + link request: activated now, prorata to its own anniversary / end of its prepaid period.
const Upgrade = ({ b, onDone, testId }) => {
  const { t, fmtDate } = useI18n();
  const fmt = useFmt();
  const [busy, setBusy] = useState(false);
  const u = b.upgrade;
  const go = async () => {
    setBusy(true);
    try {
      const res = await api.post("/dev/billing/cart/upgrade", { key_id: b.key_id, ref: b.ref, expected_ht: u.prorata_ht });
      toast.success(t("cart.upDone", { ref: b.ref }));
      if (backFrom(res)) return openTopLevel(backFrom(res));
      onDone();
    } catch (err) {
      toast.error(translateError(err, t));
      if (backFrom(err.response)) return openTopLevel(backFrom(err.response));
      onDone();
    }
    setBusy(false);
  };
  const args = { amount: fmt.eur(u.prorata_ht), date: fmtDate(u.until), monthly: fmt.eur(u.monthly_after_ht) };
  return (
    <div className="mt-3 min-w-0 space-y-2 rounded-xl border border-sky-400/40 bg-sky-950/30 p-3" data-testid={`${testId}-upgrade`}>
      <p className="break-words text-sm font-semibold text-white" data-testid={`${testId}-upgrade-price`}>
        {u.prorata_ht > 0 ? t(u.kind === "prepaid" ? "cart.upPrepaid" : "cart.upProrata", args) : t("cart.upFree", args)}</p>
      <button type="button" className="btn-gold btn-sm min-h-[44px] whitespace-normal" onClick={go} disabled={busy} data-testid={`${testId}-upgrade-apply`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />} {t("cart.upApply")}</button>
    </div>
  );
};

// « Robot trieur » per business: 3 € TTC / month for THIS REF (included for VIP / creator, never charged twice).
// Running business: Robot trieur activated now, prorata to ITS own renewal / prepaid end (store-priced), then monthly.
const RobotUpgrade = ({ b, onDone, testId }) => {
  const { t, fmtDate } = useI18n();
  const fmt = useFmt();
  const [busy, setBusy] = useState(false);
  const u = b.robot_upgrade;
  const go = async () => {
    setBusy(true);
    try {
      const res = await api.post("/dev/billing/cart/upgrade", { key_id: b.key_id, ref: b.ref, robot: true, expected_ht: u.prorata_ht });
      toast.success(t("cart.upDone", { ref: b.ref }));
      if (backFrom(res)) return openTopLevel(backFrom(res));
    } catch (err) {
      toast.error(translateError(err, t));
      if (backFrom(err.response)) return openTopLevel(backFrom(err.response));
    }
    setBusy(false);
    onDone();
  };
  const args = { amount: fmt.eur(u.prorata_ht), date: fmtDate(u.until), monthly: fmt.eur(u.monthly_after_ht) };
  return (
    <div className="mt-3 min-w-0 space-y-2 rounded-xl border border-teal-400/30 bg-teal-950/30 p-3" data-testid={`${testId}-robot-upgrade`}>
      <p className="break-words text-sm text-white"><span className="font-semibold">{t("robot.add")}</span> — {t("robot.price")}</p>
      <p className="break-words text-xs text-teal-100" data-testid={`${testId}-robot-upgrade-price`}>{u.prorata_ht > 0 ? t(u.kind === "prepaid" ? "cart.upPrepaid" : "cart.upProrata", args) : t("cart.upFree", args)}</p>
      <button type="button" className="btn-gold btn-sm min-h-[44px] whitespace-normal" onClick={go} disabled={busy} data-testid={`${testId}-robot-upgrade-apply`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />} {t("cart.upApply")}</button>
    </div>
  );
};

const RobotLine = ({ b, sel, onSel, onDone, testId }) => {
  const { t } = useI18n();
  if (b.robot) {
    return <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-teal-900/50 px-2 py-0.5 text-xs font-semibold text-teal-100" data-testid={`${testId}-robot-${b.robot}`}>
      <Bot className="h-3.5 w-3.5 shrink-0" /> {t("robot.name")} · {t(b.robot === "free" ? "robot.included" : "robot.already")}</p>;
  }
  if (b.robot_mode === "upgrade" && b.robot_upgrade && !b.upgrade) return <RobotUpgrade b={b} onDone={onDone} testId={testId} />;
  const show = (b.robot_mode === "line" && sel?.on) || b.robot_mode === "solo";
  if (!show) return null;
  const toggle = (on) => onSel(b.robot_mode === "solo" ? { ...(sel || { months: 1, employees: 0 }), robot: on, on } : { ...sel, robot: on });
  return (
    <label className="mt-3 flex min-h-[44px] min-w-0 cursor-pointer items-start gap-3 rounded-xl border border-teal-400/30 bg-teal-950/30 p-3" data-testid={`${testId}-robot`}>
      <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-teal-400" checked={Boolean(sel?.robot)} onChange={(e) => toggle(e.target.checked)} data-testid={`${testId}-robot-check`} />
      <span className="min-w-0 break-words text-sm text-white"><span className="font-semibold">{t("robot.add")}</span> — {t("robot.price")}</span>
    </label>
  );
};

const Row = ({ b, line, sel, onSel, onDone, testId }) => {
  const { t, fmtDate } = useI18n();
  const fmt = useFmt();
  const q = line?.quote;
  return (
    <li className={`min-w-0 rounded-2xl border p-4 ${sel?.on ? "border-[var(--gold)] bg-black/40" : "border-[rgba(245,197,66,0.25)] bg-black/20"}`} data-testid={testId}>
      <label className="flex min-w-0 cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-1 h-5 w-5 shrink-0 accent-[var(--gold)]" checked={Boolean(sel?.on)} disabled={!b.payable}
          onChange={(e) => onSel({ ...sel, on: e.target.checked })} aria-label={t("cart.pick", { ref: b.ref })} data-testid={`${testId}-check`} />
        <span className="min-w-0 flex-1">
          <span className="block break-words font-semibold text-white">{b.position ? <span className="me-1 text-[var(--gold)]">{t("setup.pos", { n: b.position })}</span> : null}{b.business_name || b.ref}</span>
          <span className="block break-all text-xs text-neutral-400" dir="ltr">{b.app} · {b.ref}</span>
          {b.covered && (
            <span className="mt-1 inline-flex max-w-full items-center gap-1 break-words rounded-full bg-emerald-900/50 px-2 py-0.5 text-xs font-semibold text-emerald-200" data-testid={`${testId}-covered`}>
              <BadgeCheck className="h-3.5 w-3.5 shrink-0" /> {t(COVER[b.covered])}{b.until ? ` · ${t("cart.until", { date: fmtDate(b.until) })}` : ""}
            </span>
          )}
        </span>
      </label>
      <ChangeBadges b={b} testId={testId} />
      {b.request && <Request r={b.request} testId={testId} />}
      {b.upgrade ? <Upgrade b={b} onDone={onDone} testId={testId} /> : b.live && <LiveEmployees b={b} testId={testId} />}
      <RobotLine b={b} sel={sel} onSel={onSel} onDone={onDone} testId={testId} />
      {b.robot_mode === "solo" && sel?.on && q && <p className="mt-2 break-words text-sm font-semibold text-white" data-testid={`${testId}-robot-price`}>{t("cart.line", { ht: fmt.eur(q.total_ht), ttc: fmt.eur(q.total_ttc) })}</p>}
      {sel?.on && b.payable && (
        <div className="mt-3 min-w-0 space-y-3">
          <DurationPicker value={sel.months} onChange={(m) => onSel({ ...sel, months: m })} testId={`${testId}-duration`} />
          <EmpInput value={sel.employees} onChange={(n) => onSel({ ...sel, employees: n })} testId={`${testId}-employees`} />
          {q && <p className="break-words text-sm font-semibold text-white" data-testid={`${testId}-price`}>{t("cart.line", { ht: fmt.eur(q.total_ht), ttc: fmt.eur(q.total_ttc) })}</p>}
          {q?.saving_ht > 0 && <p className="text-xs font-semibold text-emerald-300">{t("prepaid.save", { amount: fmt.eur(q.saving_ht) })}</p>}
        </div>
      )}
    </li>
  );
};

const Totals = ({ total, n, busy, off, onPay, url }) => {
  const { t } = useI18n();
  const fmt = useFmt();
  return (
    <div className="min-w-0 space-y-2 rounded-2xl border border-[var(--gold)] bg-black/40 p-4" data-testid="cart-total">
      {total && <p className="break-words font-display text-base font-bold text-white" data-testid="cart-total-amount">{t("cart.total", { ht: fmt.eur(total.total_ht), ttc: fmt.eur(total.total_ttc) })}</p>}
      {total?.saving_ht > 0 && <p className="text-sm font-semibold text-emerald-300" data-testid="cart-total-saving">{t("prepaid.save", { amount: fmt.eur(total.saving_ht) })}</p>}
      {total?.usd && <p className="text-xs text-neutral-400" data-testid="cart-total-usd">{t(total.currency === "usd" ? "prepaid.usdCharged" : "prepaid.usd", { amount: fmt.usd(total.usd.total_ht) })}</p>}
      {!n && <p className="text-sm text-amber-200" data-testid="cart-none">{t("cart.none")}</p>}
      <button type="button" className="btn-gold btn-sm min-h-[44px] w-full justify-center whitespace-normal sm:w-auto" onClick={onPay} disabled={busy || off || !n || !total} data-testid="cart-pay">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {t("cart.pay", { n })}
      </button>
      {url && <a href={url} target="_top" rel="noopener" className="btn-red btn-sm btn-cta w-full sm:w-auto" data-testid="cart-open">{t("billing.openCheckout")}</a>}
    </div>
  );
};

const useQuote = (lines, t) => {
  const [q, setQ] = useState(null);
  const key = JSON.stringify(lines);
  useEffect(() => {
    if (!lines.length) { setQ(null); return undefined; }
    let off = false;
    const id = setTimeout(() => api.post("/dev/billing/cart/quote", { lines }).then(({ data }) => !off && setQ(data))
      .catch((err) => !off && setQ({ error: translateError(err, t) })), 250);
    return () => { off = true; clearTimeout(id); };
  }, [key, t]); // eslint-disable-line react-hooks/exhaustive-deps
  return q;
};

// Client space cart: 1..N of the client's OWN businesses, one Stripe Checkout (each REF activated on its own).
export const BusinessCart = ({ app, preRef, openAll, billing }) => {
  const { t } = useI18n();
  const [list, setList] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const [appKey, setAppKey] = useState(null);
  const [adding, setAdding] = useState(false);
  const [tick, setTick] = useState(0);
  const [back, setBack] = useState(null);
  const reload = () => setTick((n) => n + 1);
  useEffect(() => {
    api.get("/dev/billing/cart", { params: app ? { app } : {} }).then(({ data }) => {
      setList(data.businesses);
      setBack(data.retour_cancel || null);
      const keys = [...new Set(data.businesses.map((b) => b.key_id))];
      setAppKey(data.app?.key_id || (keys.length === 1 ? keys[0] : null));
      const refs = (preRef || "").toUpperCase().split(",").filter(Boolean);
      setSel((old) => {
        const init = {};
        data.businesses.filter((b) => b.payable).forEach((b) => {
          const k = `${b.key_id}:${b.ref}`;
          init[k] = old[k] || { on: openAll || refs.includes(b.ref) || Boolean(b.request), months: 1,
            employees: b.request ? clamp(b.request.total) : 0, team: Boolean(b.request?.team), robot: Boolean(b.request?.robot) };
        });
        data.businesses.filter((b) => b.robot_mode === "solo").forEach((b) => {  // covered business: only its Robot trieur
          const k = `${b.key_id}:${b.ref}`;
          init[k] = old[k] || { on: Boolean(b.request?.robot), months: 1, employees: 0, robot: Boolean(b.request?.robot) };
        });
        return init;
      });
    }).catch(() => setList([]));
  }, [app, preRef, openAll, tick]);
  const lines = useMemo(() => (list || []).filter((b) => sel[`${b.key_id}:${b.ref}`]?.on)
    .map((b) => ({ key_id: b.key_id, ref: b.ref, ...sel[`${b.key_id}:${b.ref}`], on: undefined })), [list, sel]);
  const q = useQuote(lines, t);
  const byRef = Object.fromEntries((q?.lines || []).map((l) => [`${l.key_id}:${l.ref}`, l]));
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dev/billing/cart/checkout", { lines, origin_url: window.location.origin, expected_total: q?.total?.pay_ttc });
      if (isFramed()) setUrl(data.checkout_url);
      openTopLevel(data.checkout_url);
    } catch (err) {
      toast.error(translateError(err, t));
      setBusy(false);
    }
  };
  if (list === null) return null;
  return (
    <section id="panier" className="card-carbon min-w-0 space-y-4 rounded-2xl p-4 sm:p-5" data-testid="business-cart">
      <h2 className="flex items-center gap-2 break-words font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]">
        <ShoppingCart className="h-4 w-4 shrink-0" /> {t("cart.title")}
      </h2>
      <CartIntro />
      {list.some((b) => !b.confirmed) && <ConfirmList key={list.map((b) => b.ref).join()} rows={list} onDone={reload} />}
      {!list.length && <p className="text-sm text-neutral-400" data-testid="cart-empty">{t("cart.empty")}</p>}
      <ul className="min-w-0 space-y-3">
        {list.map((b) => {
          const k = `${b.key_id}:${b.ref}`;
          return <Row key={k} b={b} line={byRef[k]} sel={sel[k]} onSel={(v) => setSel((s) => ({ ...s, [k]: v }))} onDone={reload} testId={`cart-row-${b.ref}`} />;
        })}
      </ul>
      {appKey && (adding ? <AddBusiness app={appKey} next={list.length + 1} onDone={reload} />
        : <button type="button" className="btn-red btn-sm min-h-[44px]" onClick={() => setAdding(true)} data-testid="setup-add-open"><Plus className="h-4 w-4" /> {t("setup.add")}</button>)}
      {(app || appKey) && <AdvancedLink app={app || list[0]?.app_id || appKey} onDone={reload} />}
      <ChangesBox rows={list} onDone={reload} />
      {q?.error && <p className="text-sm text-amber-200" data-testid="cart-error">{q.error}</p>}
      {list.some((b) => b.payable) && billing?.status !== "vip" && <Totals total={q?.total} n={lines.length} busy={busy} off={billing?.payments_available === false} onPay={pay} url={url} />}
      {back && <button type="button" className="btn-red btn-sm min-h-[44px] max-w-full whitespace-normal" onClick={() => openTopLevel(back)} data-testid="cart-cancel-return">
        <X className="h-4 w-4 shrink-0" /> {t("cart.cancelReturn")}</button>}
      <LongerPlanBox testId="cart-longer" />
    </section>
  );
};
